import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Alert,
  Box,
  Stack,
  Typography,
  Chip,
  Button,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { ArrowBack, ArrowForward } from '@mui/icons-material'
import useIsMobile from '../../../helpers/useIsMobile'
import moment from 'moment'
import API from '../../../services/API'
import DutyCard from './DutyCard'
import AssignJourneyDialog from './AssignJourneyDialog'
import { useNavigate } from 'react-router-dom'
import Header from './Header'
import LeftPanel from './LeftPanel'
import CentrePanel from './CentrePanel'
import { SiteModeContext } from '../../../components/mainFrame/MainFrame'
import Loading from '../../loading/loadingPage'
import { getArrivalProximityCheck } from '../proximity'
import ArrivalProximityDialog from '../components/ArrivalProximityDialog'
import PortalChatDialog from '../components/PortalChatDialog'
import OutstandingJourneys from '../components/OutstandingJourneys'
import PortalRequestReviewDialog from '../components/PortalRequestReviewDialog'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'

export const PTSControlContext = createContext({})

const getWardLabel = (loc) =>
  [loc?.wardDepartmentAsAddress?.name || loc?.wardName, loc?.wardLocation]
    .filter(Boolean)
    .join(' - ')

const PTSControllerOverview = () => {
  const { setSiteMode } = useContext(SiteModeContext)
  // Below `lg` the grid can't fit the dispatch list beside the map, so the two
  // panels become switchable instead of squashing into one column.
  const isStacked = useIsMobile('lg')
  const isPhone = useIsMobile('sm')
  const [mobilePanel, setMobilePanel] = useState('list')
  const [date, setDate] = useState(moment())
  const [loading, setLoading] = useState(true)
  const [duties, setDuties] = useState([])
  const [unassigned, setUnassigned] = useState([])
  const [assignOpen, setAssignOpen] = useState(false)
  const [assignJourney, setAssignJourney] = useState(null)
  const [viewJourney, setViewJourney] = useState(null)
  const [selectedDuty, setSelectedDuty] = useState(null)
  const [selectedRoute, setSelectedRoute] = useState(null)
  const [leftColumnWidth, setLeftColumnWidth] = useState(280)
  const [arrivalPrompt, setArrivalPrompt] = useState(null)
  const [crewMessageAlert, setCrewMessageAlert] = useState(null)
  const [portalChatAlert, setPortalChatAlert] = useState(null)
  const [activePortalChat, setActivePortalChat] = useState(null)
  const [portalChatOpen, setPortalChatOpen] = useState(false)
  const [pendingRequestCount, setPendingRequestCount] = useState(0)
  const [requestReviewOpen, setRequestReviewOpen] = useState(false)
  const [connectionIssue, setConnectionIssue] = useState(false)
  const [actionError, setActionError] = useState(null)
  const crewChatCountsRef = useRef({})
  const crewChatCountsReadyRef = useRef(false)
  const crewMessageAudioRef = useRef(null)
  const seenPortalChatRequestsRef = useRef(new Set())
  const readCrewChatCountsRef = useRef(
    JSON.parse(localStorage.getItem('ptsCrewChatReadCounts') || '{}'),
  )

  const withUnreadCrewChatCounts = useCallback((sourceDuties) => {
    const readCounts = readCrewChatCountsRef.current || {}
    return (sourceDuties || []).map((duty) => ({
      ...duty,
      crewChatMessagesAwaitingReply:
        duty.crewChatMessagesSinceLastControlCount ?? 0,
      unreadCrewChatCount:
        (duty.crewChatMessageCount || 0) > (readCounts[duty._id] || 0)
          ? duty.crewChatMessagesSinceLastControlCount ?? 0
          : 0,
    }))
  }, [])

  const startLeftColumnResize = useCallback(
    (event) => {
      event.preventDefault()
      const startX = event.clientX
      const startWidth = leftColumnWidth

      const handleMouseMove = (moveEvent) => {
        setLeftColumnWidth(
          Math.min(480, Math.max(230, startWidth + moveEvent.clientX - startX)),
        )
      }

      const handleMouseUp = () => {
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }

      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
    },
    [leftColumnWidth],
  )

  useEffect(() => {
    setSiteMode('PTSControl')
  }, [setSiteMode])

  // Picking a duty from the stacked list jumps to the map/duty view so the
  // selection is actually visible on a phone.
  const selectedDutyId = selectedDuty?._id
  useEffect(() => {
    if (isStacked && selectedDutyId) setMobilePanel('map')
  }, [isStacked, selectedDutyId])

  const navigate = useNavigate()

  const playCrewMessageSound = useCallback(() => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext
      if (!AudioContext) return

      const audioContext = crewMessageAudioRef.current || new AudioContext()
      crewMessageAudioRef.current = audioContext

      if (audioContext.state === 'suspended') {
        audioContext.resume().catch(() => {})
      }

      const now = audioContext.currentTime
      const gain = audioContext.createGain()
      gain.gain.setValueAtTime(0.0001, now)
      gain.gain.exponentialRampToValueAtTime(0.08, now + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.42)
      gain.connect(audioContext.destination)
      ;[880, 1175].forEach((frequency, index) => {
        const oscillator = audioContext.createOscillator()
        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(frequency, now + index * 0.13)
        oscillator.connect(gain)
        oscillator.start(now + index * 0.13)
        oscillator.stop(now + index * 0.13 + 0.18)
      })
    } catch (e) {
      console.warn('Unable to play PTS crew message alert sound', e)
    }
  }, [])

  // Count journeys by their own status, not duties
  const counts = useMemo(() => {
    const c = {
      Waiting: 0,
      Active: 0,
      Complete: 0,
      Exceptions: 0,
      Unassigned: unassigned.length,
    }
    const waitingStatuses = ['Planned', 'Assigned']
    const activeStatuses = [
      'Crew Acknowledged',
      'Mobile',
      'On Scene',
      'Patient Onboard',
      'Transporting',
      'Arrived',
      'Handover Complete',
    ]
    const exceptionStatuses = ['Cancelled', 'No Show', 'Aborted']

    duties.forEach((duty) => {
      if (Array.isArray(duty.routes)) {
        duty.routes.forEach((r) => {
          if (Array.isArray(r.journeys)) {
            r.journeys.forEach((j) => {
              const status = j.status || 'Planned'
              if (waitingStatuses.includes(status)) c.Waiting += 1
              else if (activeStatuses.includes(status)) c.Active += 1
              else if (status === 'Completed') c.Complete += 1
              else if (exceptionStatuses.includes(status)) c.Exceptions += 1
            })
          }
        })
      }
    })
    return c
  }, [duties, unassigned.length])

  const fetchAll = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true)
      try {
        const [dutiesRes, unassignedRes, portalChatRes, pendingRequestsRes] =
          await Promise.all([
            API.get('/pts/controlroom/activeduties'),
            API.get('/pts/controlroom/unassignedjourneys'),
            API.get('/pts/dispatch/chat-requests'),
            API.get('/pts/portal-requests/pending'),
          ])

        if (pendingRequestsRes.status === 200) {
          setPendingRequestCount((pendingRequestsRes.data || []).length)
        }

        if (dutiesRes.status === 200) {
          const nextDuties = withUnreadCrewChatCounts(dutiesRes.data || [])
          if (crewChatCountsReadyRef.current) {
            const newCrewMessages = nextDuties
              .filter((duty) => {
                const previousCount = crewChatCountsRef.current[duty._id] || 0
                return (duty.crewChatMessageCount || 0) > previousCount
              })
              .sort(
                (a, b) =>
                  new Date(b.latestCrewChatMessage?.createdAt || 0) -
                  new Date(a.latestCrewChatMessage?.createdAt || 0),
              )

            if (newCrewMessages.length) {
              const duty = newCrewMessages[0]
              playCrewMessageSound()
              setCrewMessageAlert({
                dutyId: duty._id,
                callSign: duty?.vehicle?.callSign || duty?.dutyRef || 'PTS',
                message:
                  duty.latestCrewChatMessage?.message || 'New crew message',
              })
            }
          }

          crewChatCountsRef.current = nextDuties.reduce((counts, duty) => {
            counts[duty._id] = duty.crewChatMessageCount || 0
            return counts
          }, {})
          crewChatCountsReadyRef.current = true

          setDuties(nextDuties)
          setSelectedDuty((currentSelectedDuty) => {
            if (!currentSelectedDuty?._id) return currentSelectedDuty
            return (
              nextDuties.find((duty) => duty._id === currentSelectedDuty._id) ??
              currentSelectedDuty
            )
          })
        }

        if (unassignedRes.status === 200) {
          setUnassigned(unassignedRes.data)
        }

        if (portalChatRes.status === 200) {
          const requests = portalChatRes.data?.requests || []

          const unseenRequest = requests.find(
            (request) => !seenPortalChatRequestsRef.current.has(request._id),
          )

          if (unseenRequest) {
            seenPortalChatRequestsRef.current.add(unseenRequest._id)
            setPortalChatAlert(unseenRequest)
          }
        }

        setConnectionIssue(false)
      } catch (e) {
        console.error(e)
        setConnectionIssue(true)
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [playCrewMessageSound, withUnreadCrewChatCounts],
  )

  const markDutyChatRead = useCallback(
    (dutyId, count = 0) => {
      if (!dutyId) return
      const nextReadCounts = {
        ...(readCrewChatCountsRef.current || {}),
        [dutyId]: count,
      }
      readCrewChatCountsRef.current = nextReadCounts
      localStorage.setItem(
        'ptsCrewChatReadCounts',
        JSON.stringify(nextReadCounts),
      )
      setDuties((currentDuties) => withUnreadCrewChatCounts(currentDuties))
      setSelectedDuty((currentSelectedDuty) => {
        if (currentSelectedDuty?._id !== dutyId) return currentSelectedDuty
        return {
          ...currentSelectedDuty,
          unreadCrewChatCount: 0,
        }
      })
    },
    [withUnreadCrewChatCounts],
  )

  useEffect(() => {
    fetchAll()
    const interval = setInterval(() => fetchAll(true), 5000)
    return () => clearInterval(interval)
  }, [fetchAll])

  useEffect(() => {
    const markAvailable = () => {
      API.post('/pts/dispatch/presence', { available: true }).catch(() => {})
    }
    const markUnavailable = () => {
      API.post('/pts/dispatch/presence', { available: false }).catch(() => {})
    }

    markAvailable()
    const interval = setInterval(markAvailable, 15000)

    return () => {
      clearInterval(interval)
      markUnavailable()
    }
  }, [])

  const handlePortalChatRequest = async (requestID, status = 'accepted') => {
    if (!requestID) return

    try {
      const res = await API.put(`/pts/dispatch/chat-requests/${requestID}`, {
        status,
      })
      setPortalChatAlert(null)
      if (status === 'accepted' && res.status === 200) {
        setActivePortalChat(res.data)
        setPortalChatOpen(true)
      }
      await fetchAll(true)
    } catch (e) {
      console.error(e)
      setActionError('Failed to update portal chat request. Please try again.')
    }
  }

  useEffect(() => {
    if (!activePortalChat?._id) return undefined

    const loadChat = async () => {
      try {
        const res = await API.get(
          `/pts/dispatch/chat-requests/${activePortalChat._id}`,
        )
        if (res.status === 200) {
          setActivePortalChat(res.data)
        }
      } catch (e) {
        console.error(e)
      }
    }

    loadChat()
    const interval = setInterval(loadChat, 3000)

    return () => clearInterval(interval)
  }, [activePortalChat?._id])

  const sendDispatchChatMessage = async (message) => {
    if (!activePortalChat?._id) return false

    try {
      const res = await API.post(
        `/pts/dispatch/chat-requests/${activePortalChat._id}/messages`,
        { message },
      )
      if (res.status === 200) {
        setActivePortalChat(res.data)
        return true
      }
    } catch (e) {
      console.error(e)
      setActionError('Failed to send message. Please try again.')
    }

    return false
  }

  const openAssign = (j) => {
    setAssignJourney(j)
    setAssignOpen(true)
  }

  const confirmAssign = async (dutyId) => {
    if (!assignJourney?._id) return
    try {
      await API.put(`/pts/journeys/${assignJourney._id}/assign`, { dutyId })
      setAssignOpen(false)
      setAssignJourney(null)
      await fetchAll()
    } catch (e) {
      console.error(e)
      setActionError('Failed to assign journey. Please try again.')
    }
  }

  const saveJourneyStatus = async (journeyId, status) => {
    if (!journeyId || !status) return
    try {
      await API.put(`/pts/journeys/${journeyId}/status`, { status })
      await fetchAll(true)
    } catch (e) {
      console.error(e)
      setActionError('Failed to update journey status. Please try again.')
    }
  }

  const updateJourneyStatus = async (journeyId, status, journey = null) => {
    if (!journeyId || !status) return
    const arrivalTarget =
      journey?.status === 'Mobile' && status === 'On Scene'
        ? 'pickup'
        : journey?.status === 'Transporting' && status === 'Arrived'
          ? 'dropoff'
          : null
    const proximityCheck = getArrivalProximityCheck({
      status,
      journey,
      currentCoords: selectedDuty?.vehicle?.location?.coordinates,
      targetType: arrivalTarget,
    })

    if (proximityCheck) {
      setArrivalPrompt({ check: proximityCheck, journeyId, status })
      return
    }

    await saveJourneyStatus(journeyId, status)
  }

  return (
    <PTSControlContext.Provider
      value={{
        selectedDuty,
        setSelectedDuty,
        selectedRoute,
        setSelectedRoute,
        refreshControl: () => fetchAll(true),
        updateJourneyStatus,
        markDutyChatRead,
      }}
    >
      <Box
        sx={{
          p: 0,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(180deg, #0F172A 0%, #0B1220 100%)',
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(15, 23, 42, 0.85)',
          overflow: 'hidden',
        }}
      >
        <Header counts={counts} />

        {pendingRequestCount > 0 && (
          <Box
            onClick={() => setRequestReviewOpen(true)}
            sx={{
              mx: 1,
              mt: 1,
              px: 2,
              py: 1,
              borderRadius: 2,
              cursor: 'pointer',
              bgcolor: '#B45309',
              border: '1px solid #F59E0B',
              '@keyframes newRequestFlash': {
                '0%': { boxShadow: '0 0 0 0 rgba(245, 158, 11, 0.7)' },
                '70%': { boxShadow: '0 0 0 12px rgba(245, 158, 11, 0)' },
                '100%': { boxShadow: '0 0 0 0 rgba(245, 158, 11, 0)' },
              },
              animation: 'newRequestFlash 1.5s ease-out infinite',
              '&:hover': { bgcolor: '#92400E' },
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <LocalHospitalIcon sx={{ color: '#FDE68A' }} />
                <Typography sx={{ color: '#FFFFFF', fontWeight: 900 }}>
                  {pendingRequestCount === 1
                    ? 'New transport request'
                    : `${pendingRequestCount} new transport requests`}
                </Typography>
                <Typography sx={{ color: '#FDE68A', fontSize: '0.85rem' }}>
                  Hospital portal — awaiting review
                </Typography>
              </Stack>
              <Typography
                sx={{ color: '#FFFFFF', fontWeight: 800, fontSize: '0.85rem' }}
              >
                Click to review
              </Typography>
            </Stack>
          </Box>
        )}

        <OutstandingJourneys variant="dark" sx={{ mx: 1, mt: 1 }} />

        {isStacked && !loading && (
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={mobilePanel}
            onChange={(e, next) => next && setMobilePanel(next)}
            sx={{
              width: 'calc(100% - 16px)',
              mx: 1,
              mt: 1,
              '& .MuiToggleButton-root': {
                color: 'rgba(226,232,240,0.8)',
                borderColor: 'rgba(255,255,255,0.16)',
                fontWeight: 800,
                textTransform: 'none',
                '&.Mui-selected': {
                  color: '#fff',
                  backgroundColor: 'rgba(59,130,246,0.35)',
                },
                '&.Mui-selected:hover': {
                  backgroundColor: 'rgba(59,130,246,0.45)',
                },
              },
            }}
          >
            <ToggleButton value="list">
              Dispatch list ({duties.length + unassigned.length})
            </ToggleButton>
            <ToggleButton value="map">
              {selectedDuty?.vehicle?.callSign
                ? `Map · ${selectedDuty.vehicle.callSign}`
                : 'Map'}
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        {connectionIssue && !loading && (
          <Alert severity="error" variant="filled" sx={{ borderRadius: 0 }}>
            Connection issue — live duty and journey data may be out of date.
            Retrying...
          </Alert>
        )}

        {loading ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Loading />
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              p: 1,
              border: '1px solid rgba(255,255,255,0.06)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: '100%',
                minHeight: 0,
                position: 'relative',
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  lg: `${leftColumnWidth}px 10px minmax(0, 1fr)`,
                },
              }}
            >
              {/* Stacked (below lg): both panels fill the same box and the
                  inactive one is hidden with visibility (not display) so the
                  map keeps its real size and can be switched to instantly. */}
              <Box
                sx={{
                  minHeight: 0,
                  minWidth: 0,
                  position: { xs: 'absolute', lg: 'static' },
                  inset: { xs: 0, lg: 'auto' },
                  visibility: {
                    xs: mobilePanel === 'list' ? 'visible' : 'hidden',
                    lg: 'visible',
                  },
                  opacity: { xs: mobilePanel === 'list' ? 1 : 0, lg: 1 },
                  pointerEvents: {
                    xs: mobilePanel === 'list' ? 'auto' : 'none',
                    lg: 'auto',
                  },
                }}
              >
                <LeftPanel
                  duties={duties}
                  unassignedJourneys={unassigned}
                  onAssign={openAssign}
                  setViewJourney={setViewJourney}
                  fetchAll={fetchAll}
                />
              </Box>

              <Box
                onMouseDown={startLeftColumnResize}
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize dispatch list"
                sx={{
                  display: { xs: 'none', lg: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'col-resize',
                  minHeight: 0,
                  '&:before': {
                    content: '""',
                    width: 4,
                    height: '100%',
                    borderRadius: 999,
                    backgroundColor: 'rgba(148,163,184,0.2)',
                    transition: 'background-color 120ms ease',
                  },
                  '&:hover:before': {
                    backgroundColor: 'rgba(96,165,250,0.65)',
                  },
                }}
              />

              <Box
                sx={{
                  height: '100%',
                  minHeight: 0,
                  minWidth: 0,
                  position: { xs: 'absolute', lg: 'static' },
                  inset: { xs: 0, lg: 'auto' },
                  visibility: {
                    xs: mobilePanel === 'map' ? 'visible' : 'hidden',
                    lg: 'visible',
                  },
                  // MapLibre's attribution control forces its own visibility,
                  // so opacity + pointer-events keep it hidden behind the list.
                  opacity: { xs: mobilePanel === 'map' ? 1 : 0, lg: 1 },
                  pointerEvents: {
                    xs: mobilePanel === 'map' ? 'auto' : 'none',
                    lg: 'auto',
                  },
                }}
              >
                <Box sx={{ height: '100%', minHeight: 0 }}>
                  <CentrePanel duties={duties} />
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Assign dialog */}
        <AssignJourneyDialog
          open={assignOpen}
          onClose={() => setAssignOpen(false)}
          duties={duties}
          onConfirm={confirmAssign}
          journey={assignJourney}
        />

        {/* View journey (simple dialog) */}
        <Dialog
          open={!!viewJourney}
          onClose={() => setViewJourney(null)}
          fullWidth
          maxWidth="sm"
          fullScreen={isPhone}
        >
          <DialogTitle>Journey Details</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1}>
              <Typography>
                <strong>Patient:</strong>{' '}
                {`${viewJourney?.patient?.demographics?.firstName ?? ''} ${
                  viewJourney?.patient?.demographics?.lastName ?? ''
                }`}
              </Typography>
              <Typography>
                <strong>Pickup:</strong>{' '}
                {[
                  viewJourney?.pickup?.name,
                  viewJourney?.pickup?.town,
                  viewJourney?.pickup?.city,
                ]
                  .filter(Boolean)
                  .join(', ')}{' '}
                at{' '}
                <b>
                  {viewJourney?.pickup?.when?.patientReadyFrom
                    ? moment(viewJourney.pickup.when.patientReadyFrom).format(
                        'HH:mm',
                      )
                  : 'Any Time'}
                </b>
              </Typography>
              {getWardLabel(viewJourney?.pickup) && (
                <Typography color="text.secondary">
                  <strong>Pickup ward:</strong> {getWardLabel(viewJourney.pickup)}
                </Typography>
              )}
              <Typography>
                <strong>Dropoff:</strong>{' '}
                {[
                  viewJourney?.dropoff?.name,
                  viewJourney?.dropoff?.town,
                  viewJourney?.dropoff?.city,
                ]
                  .filter(Boolean)
                  .join(', ')}{' '}
                at{' '}
                <b>
                  {viewJourney?.dropoff?.when?.targetArrivalTime
                    ? moment(viewJourney.dropoff.when.targetArrivalTime).format(
                        'HH:mm',
                      )
                  : 'Any Time'}
                </b>
              </Typography>
              {getWardLabel(viewJourney?.dropoff) && (
                <Typography color="text.secondary">
                  <strong>Dropoff ward:</strong>{' '}
                  {getWardLabel(viewJourney.dropoff)}
                </Typography>
              )}
              {viewJourney?.assigned?.vehicle && (
                <Typography>
                  <strong>Assigned Vehicle:</strong>{' '}
                  {viewJourney?.assigned?.vehicle?.callSign || '—'}
                </Typography>
              )}
              <Button
                onClick={() => {
                  window.open('/pts/bookings/' + viewJourney.booking)
                }}
              >
                View Booking
              </Button>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewJourney(null)}>Close</Button>
          </DialogActions>
        </Dialog>

        <ArrivalProximityDialog
          open={!!arrivalPrompt}
          check={arrivalPrompt?.check}
          onCancel={() => setArrivalPrompt(null)}
          onConfirm={async () => {
            const { journeyId, status } = arrivalPrompt || {}
            setArrivalPrompt(null)
            await saveJourneyStatus(journeyId, status)
          }}
        />
        <Snackbar
          open={!!crewMessageAlert}
          autoHideDuration={8000}
          onClose={() => setCrewMessageAlert(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            severity="info"
            variant="filled"
            onClose={() => setCrewMessageAlert(null)}
            sx={{ maxWidth: 360 }}
          >
            <Typography
              sx={{ fontWeight: 900, fontSize: '0.9rem', color: 'white' }}
            >
              New crew message from {crewMessageAlert?.callSign}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.8rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {crewMessageAlert?.message}
            </Typography>
          </Alert>
        </Snackbar>
        <Snackbar
          open={!!portalChatAlert}
          onClose={() => setPortalChatAlert(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity="warning"
            variant="filled"
            onClose={() => setPortalChatAlert(null)}
            sx={{ maxWidth: 420 }}
            action={
              <Stack direction="row" gap={1}>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() =>
                    handlePortalChatRequest(portalChatAlert?._id, 'accepted')
                  }
                >
                  Accept
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() =>
                    handlePortalChatRequest(portalChatAlert?._id, 'closed')
                  }
                >
                  Dismiss
                </Button>
              </Stack>
            }
          >
            <Typography
              sx={{ fontWeight: 900, fontSize: '0.9rem', color: 'white' }}
            >
              Portal chat request
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'white' }}>
              {portalChatAlert?.requester?.name || 'Hospital portal user'}
              {portalChatAlert?.ward?.label
                ? ` from ${portalChatAlert.ward.label}`
                : ''}{' '}
              is asking to chat.
            </Typography>
          </Alert>
        </Snackbar>
        <Snackbar
          open={!!actionError}
          autoHideDuration={6000}
          onClose={() => setActionError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert
            severity="error"
            variant="filled"
            onClose={() => setActionError(null)}
            sx={{ maxWidth: 360 }}
          >
            {actionError}
          </Alert>
        </Snackbar>
        <PortalChatDialog
          open={portalChatOpen}
          request={activePortalChat}
          currentRole="Dispatch"
          title="Hospital portal chat"
          onClose={() => setPortalChatOpen(false)}
          onSend={sendDispatchChatMessage}
        />
        <PortalRequestReviewDialog
          open={requestReviewOpen}
          onClose={() => setRequestReviewOpen(false)}
          onChanged={() => fetchAll(true)}
        />
      </Box>
    </PTSControlContext.Provider>
  )
}

export default PTSControllerOverview
